import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Layout from '../layout/Layout'
import MobilePromotionForm from '../molecules/MobilePromotionForm'
import PromotionPhotosField from '../molecules/PromotionPhotosField'
import ChevronRightSvg from '../other/svg/ChevronRightSvg'
import {
  MobilePromotionFormData,
  mobilePromotionFormFromMotorbike,
  mobilePromotionHighlights
} from 'src/helpers/mobilePromotionFormValidation'
import { FETCH_BRANDS } from 'src/redux/brandsReducer'
import { FETCH_CHANNELS } from 'src/redux/integrationsReducer'
import { FETCH_MOBILE_REFERENCE_VALUES } from 'src/redux/mobileReferenceReducer'
import { FETCH_MOTORBIKE, RESET_MOTORBIKE } from 'src/redux/motorbikesReducer'
import { FETCH_MOTORBIKE_PHOTOS } from 'src/redux/photosReducer'
import { CREATE_PROMOTION, FETCH_PROMOTIONS, RESET_PROMOTION } from 'src/redux/promotionsReducer'
import { RootReducer } from 'src/redux/rootReducer'

const PromotionCreate = (): JSX.Element => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const portal = searchParams.get('portal') ?? ''
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const brands = useSelector((state: RootReducer) => state.brandsReducer.brands)
  const referenceColors = useSelector((state: RootReducer) => state.mobileReferenceReducer.colors)
  const referenceCategories = useSelector((state: RootReducer) => state.mobileReferenceReducer.categories)
  const referenceGearBoxes = useSelector((state: RootReducer) => state.mobileReferenceReducer.gearBoxes)
  const referenceDriveTypes = useSelector((state: RootReducer) => state.mobileReferenceReducer.driveTypes)
  const referenceVatRates = useSelector((state: RootReducer) => state.mobileReferenceReducer.vatRates)
  const motorbike = useSelector((state: RootReducer) => {
    const { motorbike: current, motorbikeRequestId } = state.motorbikesReducer
    if (current === null || motorbikeRequestId !== id) {
      return null
    }
    return current
  })
  const motorbikeLoaded = useSelector((state: RootReducer) => state.motorbikesReducer.motorbikeLoaded)
  const motorbikeRequestId = useSelector((state: RootReducer) => state.motorbikesReducer.motorbikeRequestId)
  const loading = useSelector((state: RootReducer) => state.promotionsReducer.loading)
  const error = useSelector((state: RootReducer) => state.promotionsReducer.error)
  const apiError = useSelector((state: RootReducer) => state.promotionsReducer.errorMessage)
  const created = useSelector((state: RootReducer) => state.promotionsReducer.created)
  const loaded = useSelector((state: RootReducer) => state.promotionsReducer.loaded)
  const promotionExists = useSelector((state: RootReducer) =>
    state.promotionsReducer.promotions.some((promotion) => promotion.portal === portal)
  )
  const vehiclePath = typeof id === 'string' ? `/selling/vehicles/${id}` : '/selling/vehicles'
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<string[]>([])
  const [selectedCoverId, setSelectedCoverId] = useState<string | null>(null)
  const [photosTouched, setPhotosTouched] = useState(false)
  const photosState = useSelector((state: RootReducer) => state.photosReducer)
  const photos = photosState.motorbikeId === id ? photosState.photos : []
  const maxImages = useSelector((state: RootReducer) => {
    const channel = state.integrationsReducer.channels.find((item) => item.uniq_name === 'mobile.de')
    const limit = channel?.activation?.max_images
    return typeof limit === 'number' ? limit : null
  })

  useEffect(() => {
    dispatch(FETCH_BRANDS())
    dispatch(FETCH_CHANNELS())
    dispatch(FETCH_MOBILE_REFERENCE_VALUES())
    if (typeof id === 'string') {
      dispatch(FETCH_PROMOTIONS(id))
      dispatch(FETCH_MOTORBIKE_PHOTOS(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (typeof id === 'string') {
      dispatch(FETCH_MOTORBIKE(id))
    }
    return () => {
      dispatch(RESET_MOTORBIKE())
    }
  }, [dispatch, id])

  useEffect(() => {
    if (portal !== 'mobile.de' || typeof id !== 'string') {
      navigate(vehiclePath)
      return
    }
    if (loaded && promotionExists) {
      navigate(vehiclePath)
    }
    if (motorbikeRequestId === id && motorbikeLoaded && motorbike === null) {
      navigate(vehiclePath)
    }
  }, [id, loaded, motorbike, motorbikeLoaded, motorbikeRequestId, navigate, portal, promotionExists, vehiclePath])

  useEffect(() => {
    return () => {
      dispatch(RESET_PROMOTION())
    }
  }, [dispatch])

  useEffect(() => {
    if (photosTouched || typeof id !== 'string' || photosState.motorbikeId !== id) {
      return
    }
    const ordered = [...photosState.photos].sort((left, right) => left.position - right.position)
    const ids =
      maxImages === null ? ordered.map((photo) => photo.id) : ordered.slice(0, maxImages).map((photo) => photo.id)
    setSelectedPhotoIds(ids)
    setSelectedCoverId(ids[0] ?? null)
  }, [photosTouched, id, photosState.motorbikeId, photosState.photos, maxImages])

  useEffect(() => {
    if (!created) {
      return
    }
    dispatch(RESET_PROMOTION())
    navigate(vehiclePath)
  }, [created, dispatch, navigate, vehiclePath])

  const handleCancel = (): void => {
    navigate(vehiclePath)
  }

  const handleSubmit = (values: MobilePromotionFormData): void => {
    if (typeof id !== 'string' || portal !== 'mobile.de') {
      return
    }
    dispatch(
      CREATE_PROMOTION({
        ...values,
        motorbikeId: id,
        portal,
        highlights: mobilePromotionHighlights(values),
        photoIds: selectedPhotoIds,
        coverPhotoId: selectedCoverId
      })
    )
  }

  return (
    <Layout withoutToolbar>
      <div className="flex flex-col justify-start items-start gap-4 h-[calc(100vh-100px)]">
        <div className="self-stretch min-h-14 bg-Estigroup-primary rounded-lg border border-neutral-200 flex flex-col justify-start items-center">
          <div className="self-stretch px-4 py-3 inline-flex justify-start items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat leading-5">
              {t('my_vehicles.promotion')}
            </button>
            <ChevronRightSvg className="size-3.5 text-foregrounds-fg-on-color" />
            <div className="text-foregrounds-fg-on-color text-xl font-semibold font-Montserrat leading-5">
              {t('my_vehicles.add_promotion')}
            </div>
          </div>
        </div>
        {motorbike !== null && loaded && !promotionExists && (
          <PromotionPhotosField
            photos={photos}
            selectedIds={selectedPhotoIds}
            coverId={selectedCoverId}
            maxImages={maxImages}
            onChange={(ids, coverId) => {
              setPhotosTouched(true)
              setSelectedPhotoIds(ids)
              setSelectedCoverId(coverId)
            }}
          />
        )}
        {motorbike !== null && loaded && !promotionExists && (
          <MobilePromotionForm
            initialValues={mobilePromotionFormFromMotorbike(motorbike)}
            brands={brands}
            referenceColors={referenceColors}
            referenceCategories={referenceCategories}
            referenceGearBoxes={referenceGearBoxes}
            referenceDriveTypes={referenceDriveTypes}
            referenceVatRates={referenceVatRates}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
            error={error}
            apiError={apiError}
          />
        )}
      </div>
    </Layout>
  )
}

export default PromotionCreate
